import { getDocumentDimensions } from '~shared/utils/dom';

import {
  LEAVE_TIMEOUT,
  MOUSE_REST_TIMEOUT,
  TooltipEvent,
  context,
  transition,
} from './state';
import { PRect, Position } from './types';

/**
 * Manages when the user "rests" on an element. Keeps the interface from being
 * flashing tooltips all the time as the user moves the mouse around the screen.
 */
let restTimeout: number;

export function startRestTimer() {
  window.clearTimeout(restTimeout);
  restTimeout = window.setTimeout(
    () => transition(TooltipEvent.REST),
    MOUSE_REST_TIMEOUT
  );
}

export function clearRestTimer() {
  window.clearTimeout(restTimeout);
}

// Manages the delay to hide the tooltip after rest leaves.
let leavingVisibleTimer: number;

export function startLeavingVisibleTimer() {
  window.clearTimeout(leavingVisibleTimer);
  leavingVisibleTimer = window.setTimeout(
    () => transition(TooltipEvent.TIME_COMPLETE),
    LEAVE_TIMEOUT
  );
}

export function clearLeavingVisibleTimer() {
  window.clearTimeout(leavingVisibleTimer);
}

/**
 * Allows us to come on back later w/o entering something else first after the
 * user leaves or dismisses.
 */
export function clearContextId() {
  context.id = null;
}

// feels awkward when it's perfectly aligned w/ the trigger
const OFFSET = 8;

export function getStyles(
  position: Position,
  triggerRect: PRect,
  tooltipRect: PRect
): React.CSSProperties {
  const haventMeasuredTooltipYet = !tooltipRect;

  if (haventMeasuredTooltipYet) {
    return { visibility: 'hidden' };
  }

  return position(triggerRect, tooltipRect);
}

export const positionDefault: Position = (triggerRect, tooltipRect) => {
  const { width: windowWidth, height: windowHeight } = getDocumentDimensions();

  if (!triggerRect || !tooltipRect) {
    return {};
  }

  const collisions = {
    top: triggerRect.top - tooltipRect.height < 0,
    right: windowWidth < triggerRect.left + tooltipRect.width,
    bottom: windowHeight < triggerRect.bottom + tooltipRect.height + OFFSET,
    left: triggerRect.left - tooltipRect.width < 0,
  };
  const directionRight = collisions.right && !collisions.left;
  const directionUp = collisions.bottom && !collisions.top;

  return {
    left: directionRight
      ? `${triggerRect.right - tooltipRect.width + window.pageXOffset}px`
      : `${triggerRect.left + window.pageXOffset}px`,
    top: directionUp
      ? `${
          triggerRect.top - OFFSET - tooltipRect.height + window.pageYOffset
        }px`
      : `${
          triggerRect.top + OFFSET + triggerRect.height + window.pageYOffset
        }px`,
  };
};

/**
 * Center the tooltip, unless colliding with boundaries.
 */
export const centered = (triggerRect, tooltipRect) => {
  if (!triggerRect || !tooltipRect) {
    return {};
  }

  const triggerCenter = triggerRect.left + triggerRect.width / 2;
  const left = triggerCenter - tooltipRect.width / 2;
  const maxLeft = document.documentElement.clientWidth - tooltipRect.width;

  return {
    left: Math.min(Math.max(2, left), maxLeft) + window.scrollX,
    top: triggerRect.bottom + 6 + window.scrollY,
  };
};
